import { useState } from 'react'
import { Card } from '../Card'
import { isHeldBack, type ModerationAction, type ModerationItem } from '../../lib/moderation'

/**
 * One item in the moderator's queue.
 *
 * ── THE POST IS SHOWN IN FULL, AND THE VERDICT IS SHOWN SECOND ──────────────
 *
 * Deliberately in that order. The obvious layout leads with the machine's
 * verdict and puts the post underneath as evidence, which invites the reviewer
 * to confirm a judgement already made. The PPE observation from 4 Sep is the
 * reason to resist that: a crew photo with no safety glasses and no caption
 * PASSED, while a comparable photo captioned #noPPE was flagged with the
 * missing glasses named — two data points suggesting the check reads the
 * caption more than the image. A reviewer who reads the verdict first will find
 * the rule it names in the photograph whether or not it is there.
 *
 * So: the post, then what the check said, then the actions.
 *
 * ── WHICH ACTIONS APPEAR ────────────────────────────────────────────────────
 *
 * A held-back post (state 'blocked', never published) offers Release or
 * Remove. A published one offers Dismiss or Remove. Dismiss on an unpublished
 * post is not merely hidden here — resolve_moderation raises on it, because
 * dismiss only clears the mark, which on a blocked post leaves it invisible
 * and looking approved. The hiding is courtesy; the refusal is the control.
 *
 * ── REMOVE ASKS FOR A NOTE AND THE OTHERS DO NOT ────────────────────────────
 *
 * A takedown is the only one of the three that destroys something and cannot
 * be undone here — resolve_moderation refuses to act twice on a removed post
 * ("recreating it is a new post"). The note is what an appeal points at, and
 * FED-16's appeal UI will read it. The database defaults a note when none is
 * given, so this is a prompt rather than a validation.
 */
export function QueueItem({
  item,
  busy,
  onAct,
}: {
  item: ModerationItem
  busy: boolean
  onAct: (action: ModerationAction, note?: string) => void
}) {
  const [note, setNote] = useState('')
  const [confirmingRemove, setConfirmingRemove] = useState(false)
  const held = isHeldBack(item)

  return (
    <Card
      className="queueitem"
      title={item.title || 'Untitled post'}
      action={
        <span className={`vpill vpill--${item.failedOpen ? 'unchecked' : item.verdict}`}>
          {item.failedOpen ? 'Published unchecked' : item.verdict === 'block' ? 'Blocked' : 'Flagged'}
        </span>
      }
    >
      <p className="queueitem__meta">
        {item.bylineName
          ? `${item.bylineName}, posted by ${item.authorName ?? 'an unknown author'}`
          : (item.authorName ?? 'Unknown author')} ·{' '}
        <time dateTime={item.checkedAt}>{formatWhen(item.checkedAt)}</time> ·{' '}
        {held ? (
          <strong className="queueitem__held">Held back — not published</strong>
        ) : (
          'Published'
        )}
      </p>

      {item.body && <p className="queueitem__body">{item.body}</p>}

      {item.photos.length > 0 && (
        <ul className="queueitem__photos">
          {item.photos.map((p) => (
            <li key={p.id}>
              {p.url ? (
                // width/height are NOT NULL in the schema so the box is
                // reserved before the image loads. 019's reasoning was about
                // the feed reflowing under a reader's thumb; it matters here
                // too, because a reviewer clicking Remove as the layout jumps
                // is clicking on the wrong post.
                <img
                  src={p.url}
                  alt={p.alt ?? ''}
                  width={p.width}
                  height={p.height}
                  loading="lazy"
                />
              ) : (
                <p className="empty">This photo could not be loaded.</p>
              )}
              {/* Shown because a missing or misleading alt text is itself a
                  guideline matter, and because the check reads it. */}
              <p className="queueitem__alt">
                {p.alt ? `Alt: ${p.alt}` : 'No alt text'}
              </p>
            </li>
          ))}
        </ul>
      )}

      <div className="queueitem__verdict">
        <h3 className="queueitem__vhead">
          What the check said
          <span className="queueitem__policy">policy v{item.policyVersion}</span>
        </h3>

        {item.failedOpen ? (
          <p className="queueitem__reason">
            The check did not run, so this published without being graded. That
            is the fail-open posture (B09), not a pass — nobody has looked at it
            yet.
          </p>
        ) : (
          <>
            {item.rulesHit.length > 0 && (
              <ul className="queueitem__rules">
                {item.rulesHit.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
            )}
            {item.reason && <p className="queueitem__reason">{item.reason}</p>}
            {item.reasoning && item.reasoning !== item.reason && (
              <details className="queueitem__reasoning">
                <summary>Full reasoning</summary>
                <p>{item.reasoning}</p>
              </details>
            )}
          </>
        )}
      </div>

      <div className="queueitem__actions">
        {held ? (
          <button
            type="button"
            className="btn btn--primary btn--sm"
            disabled={busy}
            onClick={() => onAct('release')}
          >
            Release and publish
          </button>
        ) : (
          <button
            type="button"
            className="btn btn--primary btn--sm"
            disabled={busy}
            onClick={() => onAct('dismiss')}
          >
            Clear the flag
          </button>
        )}

        {!confirmingRemove ? (
          <button
            type="button"
            className="btn btn--danger btn--sm"
            disabled={busy}
            onClick={() => setConfirmingRemove(true)}
          >
            {held ? 'Refuse and remove' : 'Take it down'}
          </button>
        ) : (
          <div className="queueitem__confirm">
            <label>
              <span>Why? The author and any appeal will see this.</span>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder={
                  held
                    ? 'Blocked by the check; declined to release because…'
                    : 'Removed after publication because…'
                }
              />
            </label>
            <div className="queueitem__confirmrow">
              <button
                type="button"
                className="btn btn--danger btn--sm"
                disabled={busy}
                onClick={() => onAct('remove', note)}
              >
                {busy ? 'Working…' : 'Confirm removal'}
              </button>
              <button
                type="button"
                className="btn btn--quiet"
                disabled={busy}
                onClick={() => {
                  setConfirmingRemove(false)
                  setNote('')
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </Card>
  )
}

function formatWhen(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}
